import { 
  useContext, 
  useEffect,
  useRef, 
  useState 
} from "react"

import { 
  DataTable, 
  DataTableSessionHeader,
  DeleteLessonsConfirmationModal,
  DuplicateModal,
  EditGroupTitleModal,
  EllipsisDropdown,  
  EmailPreviewModal,
  EmptyPlaceholder, 
  EventMenu,
  EventPageHeader, 
  EventViewsTracker,
  GroupBookingModal,
  GroupLessonNoteModal,
  GroupLessonStudentsDiffModal,
  HelpPopover,
  LessonSelectorModal,
  LocationsModal,
  NotificationEmailsQueuedModal,
  Page,
  ReassignModal,
} from "@components"

import { NotificationContext, UserContext } from "@context"

import { 
  CREATE_BOOKING_SLOT_MUTATION,
  DELETE_GROUP_LESSON_NOTE,
  DUPLICATE_GROUP_LESSONS_MUTATION,
  GET_GROUP_BOOKINGS_XLS_DOWNLOAD_LINK,
  GET_GROUP_SLOTS,
  GET_LOCATIONS,
  GET_EVENT_SYNC_REPORT,
  GET_PREVIEW_DELETED_BOOKING_SLOTS_EMAIL,
  GET_SESSION, 
  GET_SESSION_GROUP_LESSON_BOOKING_COUNTS,
  GET_SESSION_GROUPS_XLS_DOWNLOAD_LINK,
  REASSIGN_GROUP_LESSONS_MUTATION,
  REFRESH_SB_ACCOUNTS_MUTATION,
  UPDATE_GROUP_MUTATION,
  UPDATE_GROUPS_LOCATION_MUTATION,
  UPDATE_GROUP_LESSON_NOTE,
  UPDATE_SESSION_LESSONS_MUTATION,
} from "@graphql"

import { 
  downloadFile, 
  getGraphQLErrorMessage, 
  hasExpired, 
  mySqlToLocal, 
  stringToColor 
} from "@helpers"

import { 
  useColumnManager,
  useSetActiveSidebarLink, 
  useSessionSelectorSidebar, 
  useSetCurrentEvent 
} from "@hooks"

import { 
  useLazyQuery, 
  useMutation, 
  useQuery 
} from "@apollo/client"

import { 
  Button, 
  Checkbox, 
  Chip,
  Spinner,
} from "@heroui/react"

import { useParams } from "react-router-dom"

import { 
  columns, 
  defaultColumns, 
  downloadOptions, 
  filters 
} from "./data"

import { RectangleGroupIcon } from "@heroicons/react/24/outline"
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid"
import { Stats } from "./Stats"

import { 
  groupBy, 
  isEmpty, 
  map, 
  orderBy, 
} from "lodash"

const ManageGroups = () => {
  const { event: eventId } = useParams()
  const { user, isLoadingCurrentEvent } = useContext(UserContext)
  const { notify } = useContext(NotificationContext)

  const [addingMisLessons, setAddingMisLessons] = useState(false)
  const [currentSessionId, setCurrentSessionId] = useState(null)
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState({ bookingSlotIds:[], totalBookingSlots: 0, show: false, error: null })
  const [duplicateModal, setDuplicateModal] = useState({ group: null, lessons: null, selectedLessons: null, show: false })
  const [editGroupTitleModal, setEditGroupTitleModal] = useState({ group: null, show: false })
  const [groupBookingModal, setGroupBookingModal] = useState({ group: null, show: false, loading: false, slots: [] })
  const [groupLessonNoteModal, setGroupLessonNoteModal] = useState({ group: null, lesson: null, groupLessonNote: null, show: false })
  const [groupLessonStudentsDiffModal, setGroupLessonStudentsDiffModal] = useState({ data: null, show: false })
  const [isBookingSlot, setIsBookingSlot] = useState(false)
  const [isDeletingLessons, setIsDeletingLessons] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isDownloadingEventSyncReport, setIsDownloadingEventSyncReport] = useState(false)
  const [isDownloadingGroupBookingsXls, setIsDownloadingGroupBookingsXls] = useState(false)
  const [isDuplicating, setIsDuplicating] = useState(null)
  const [isReassigning, setIsReassigning] = useState(null)
  const [isUpdatingGroupLessonNote, setIsUpdatingGroupLessonNote] = useState(null)
  const [isUpdatingGroup, setIsUpdatingGroup] = useState(null)
  const [locationsModal, setLocationsModal] = useState({ group: null, show: false, loading: false })
  const [sbAccounts, setSbAccounts] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedFilters, setSelectedFilters] = useState([])
  const [selectedIds, setSelectedIds] = useState(new Set([]))
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false)
  const [showLessonSelectorModal, setShowLessonSelectorModal] = useState(false)
  const [showNotificationEmailsQueuedModal, setShowNotificationEmailsQueuedModal] = useState(false)
  const [sortDescriptor, setSortDescriptor] = useState({ column: "group", direction: "ascending" })
  const [reassignModal, setReassignModal] = useState({ group: null, lessons: null, selectedLessons: null, show: false })
  const [refetching, setRefetching] = useState(false)
  
  useSetActiveSidebarLink("EVENT_MANAGE_GROUPS", "EVENT_MANAGE")
  useSetCurrentEvent(eventId)
  const [selectedColumns, setSelectedColumns] = useColumnManager(columns, "event.manage.groups.columns", defaultColumns);
  const { openSessionSelectorSidebar, closeSessionSelectorSidebar } = useSessionSelectorSidebar()
  
  const hasFetchedSBAccounts = useRef(false);

  /**
   * Mutations
   */
  const [createBookingSlot] = useMutation(CREATE_BOOKING_SLOT_MUTATION, {
    onCompleted: () => notify({ title: "Success", type: "success", content: "Slot booked successfully", open: true }),
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while booking the slot."), open: true })
  })

  const [deleteGroupLessonNote] = useMutation(DELETE_GROUP_LESSON_NOTE, {
    onCompleted: () => {
      sessionQuery.refetch().then(() => {
        setGroupLessonNoteModal(prevState => ({ ...prevState, show: false }))
        setIsUpdatingGroupLessonNote(false)
      })
    }
  })

  const [duplicateGroupLessons] = useMutation(DUPLICATE_GROUP_LESSONS_MUTATION, {
    onCompleted: () => {
      sessionQuery.refetch().then((response) => {
        setDuplicateModal({ group: null, lessons: null, selectedLessons: null, show: false })
        setIsDuplicating(false)
      })
    },

    onError: (errors) => {
      setIsDuplicating(false)
      notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while duplicating group lessons."), open: true })
    }
  })

  const [reassignGroupLessons] = useMutation(REASSIGN_GROUP_LESSONS_MUTATION, {
    onCompleted: () => {
      sessionQuery.refetch().then((response) => {
        setReassignModal({ group: null, lessons: null, selectedLessons: null, show: false })
        setIsReassigning(false)
      })
    },
    
    onError: (errors) => {
      setIsReassigning(false)
      notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while reassigning group lessons."), open: true })
    }
  })
  
  const [updateGroup] = useMutation(UPDATE_GROUP_MUTATION, {
    onCompleted: (response) => {
      setIsUpdatingGroup(false)
    },

    update(cache, { data: { updateGroup } }) {
      cache.modify({
        id: cache.identify({ __typename: "Group", id: updateGroup.id }),
        fields: {
          title() { return updateGroup.title; },
          location() { return updateGroup.location; }
        }
      });
    },

    onError: (errors) => {
      setIsUpdatingGroup(false)
      notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while updating the group."), open: true })
    }
  })

  const [updateGroupsLocation,  { loading: isUpdatingGroupsLocation }] = useMutation(UPDATE_GROUPS_LOCATION_MUTATION, {
    onCompleted: (response) => {
      const location = response?.updateGroupsLocation[0]?.location?.name || "N/A"
      notify({ title: "Success", type: "success", content: `${response?.updateGroupsLocation.length} group location(s) updated to ${location}`, open: true })
    },
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while updating the group."), open: true })
  })

  const [updateGroupLessonNote] = useMutation(UPDATE_GROUP_LESSON_NOTE, {
    onCompleted: (response) => {
      sessionQuery.refetch().then(() => {
        setGroupLessonNoteModal(prevState => ({ ...prevState, show: false }))
        setIsUpdatingGroupLessonNote(false)
      })
    },

    onError: (errors) => {
      setIsUpdatingGroupLessonNote(false)
      notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while updating group lesson notes."), open: true })
      setGroupLessonNoteModal(prevState => ({ ...prevState, show: false }))
    }
  })
  
  const [updateSessionLessons] = useMutation(UPDATE_SESSION_LESSONS_MUTATION)
  
  /**
   * Queries
  */
  const [ getEmailPreview, { data: cancellationEmailPreview, loading: isLoadingCancellationEmailPreview }] = useLazyQuery(GET_PREVIEW_DELETED_BOOKING_SLOTS_EMAIL)
  
  const [ getLocations, { data: locations }] = useLazyQuery(GET_LOCATIONS, {
    variables: { clientId: user?.me?.client?.id },
    fetchPolicy: "cache-first",
    onError: (errors) => {
      setLocationsModal(prevState => ({ ...prevState, loading: false }))
      notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while fetching locations."), open: true })
    }
  })

  const [getEventSyncReportDownloadLink] = useLazyQuery(GET_EVENT_SYNC_REPORT, {
    variables: { eventId },
    fetchPolicy: "network-only",
    onCompleted: data => {
      downloadFile(data.eventSyncReport.filename, data.eventSyncReport.url)
    }
  })

  const [getGroupBookingsXlsDownloadLink] = useLazyQuery(GET_GROUP_BOOKINGS_XLS_DOWNLOAD_LINK, {
    fetchPolicy: "network-only",
    onCompleted: data => {
      downloadFile(data.groupBookingsXlsDownload.filename, data.groupBookingsXlsDownload.url)
      setIsDownloadingGroupBookingsXls(false)
    },
  })

  const [getGroupSlots] = useLazyQuery(GET_GROUP_SLOTS, { 
    fetchPolicy: "no-cache",
    onError: (errors) => {
      setGroupBookingModal(prevState => ({ ...prevState, loading: false }))
      notify({ title: "Error", type: "danger", content: "Cannot fetch group slots", open: true })
    }
  })

  const [ getSessionGroupsXlsDownloadLink ] = useLazyQuery(GET_SESSION_GROUPS_XLS_DOWNLOAD_LINK, {
    variables: { 
      input: {
        session_id: currentSessionId 
      }
    },

    fetchPolicy: "network-only",
    
    onCompleted: data => {
      downloadFile(data.sessionGroupsXlsDownload.filename, data.sessionGroupsXlsDownload.url)
      setIsDownloading(false)
    },
  })

  const [sessionGroupLessonBookingCounts] = useLazyQuery(GET_SESSION_GROUP_LESSON_BOOKING_COUNTS, {
    variables: { sessionId: currentSessionId },
    fetchPolicy: "network-first",
    skip: currentSessionId === null,
  })

  const sessionQuery = useQuery(GET_SESSION, {
    variables: {
      sessionId: currentSessionId,
    },
    skip: currentSessionId === null,
    fetchPolicy: "cache-and-network",
  })

  // Use REFRESH_SB_ACCOUNTS_MUTATION instead of GET_SB_ACCOUNTS to ensure we get the latest data
  const [refreshAccounts, {loading: isRefreshingAccounts}] = useMutation(REFRESH_SB_ACCOUNTS_MUTATION, {
    variables: { input: { client_id: user.me.client.id } },
    fetchPolicy: "no-cache",
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while retrieving SB accounts."), open: true }),
    onCompleted: (data) => setSbAccounts(data?.refreshSBAccounts || []),
  })

  /**
   * Effects
   */
  useEffect(() => {
    if (!isLoadingCurrentEvent) {
      setCurrentSessionId(user.currentEvent.sessions[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingCurrentEvent]);

  useEffect(() => {
    // Check if session query has finished loading before firing the mutation
    if (!sessionQuery.loading && sessionQuery.data && !hasFetchedSBAccounts.current) {
      refreshAccounts();
      hasFetchedSBAccounts.current = true;
    }
  }, [refreshAccounts, sessionQuery.loading, sessionQuery.data]);
  
  /**
   * Functions
   */
  const getBookingCountsByGroupLesson = (session) => {
    return session.groupLessonBookingCounts.reduce((bookingCountsByLesson, count) => {
      bookingCountsByLesson[`${count.group_id}-${count.lesson_id}`] = count.no_of_bookings
      return bookingCountsByLesson
    }, {})
  }

  const getFilteredOrderedTableData = (tableData) => {
    const orderedTableData = orderBy(tableData, [sortDescriptor.column], [sortDescriptor.direction === "ascending" ? "asc" : "desc"])
    if (selectedFilters.includes("has_bookings")) return orderedTableData.filter(item => item.groupLessonBookingCount > 0)
    if (selectedFilters.includes("no_bookings")) return orderedTableData.filter(item => item.groupLessonBookingCount === 0)
    if (selectedFilters.includes("student_mismatch")) return orderedTableData.filter(item => item.groupLessonStudentCount !== item.studentCount)
    if (selectedFilters.includes("unassigned")) return orderedTableData.filter(item => item.isUnassigned)
    if (selectedFilters.includes("has_account")) return orderedTableData.filter(item => item.hasSBAccount === true)
    if (selectedFilters.includes("no_account")) return orderedTableData.filter(item => item.hasSBAccount === false)
      
    return orderedTableData
  }

  const getGroupsById = (sessionGroups) => {
    return sessionGroups.reduce((groups, group) => {
      groups[group.id] = group
      return groups
    }, {})
  }

  const getHasSBAccount = (email) => {
    return !hasFetchedSBAccounts.current || isRefreshingAccounts ? null : sbAccounts?.some(account => account.email === email)
  }

  const getMultipleInitialLocationId = () => {
    const selectedGroupIds = new Set([...selectedIds].map(item => item.split('-')[0]))

    const locationIds = [
      ...session?.groups
        ?.filter(group => selectedGroupIds.has(group.id))
        .reduce((acc, group) => acc.add(group?.location?.id), new Set())
    ]
    
    return locationIds.length === 1 ? locationIds[0] : null
  }

  const getLessonsToDeletePayload = () => {
    return Array.from(selectedIds).reduce((lessonIdsByGroup, id) => {
      const [groupId, lessonId] = id.split("-")
      const groupIndex = lessonIdsByGroup.findIndex((group) => group.group_id === groupId)

      groupIndex === -1 
        ? lessonIdsByGroup.push({ group_id: groupId, lesson_ids: [lessonId] })
        : lessonIdsByGroup[groupIndex].lesson_ids.push(lessonId)
      
      return lessonIdsByGroup
    }, [])
  }

  const getStudentCountsByGroupLesson = (session) => {
    return session.groupLessonStudentCounts.reduce((studentCountsByLesson, count) => {
      studentCountsByLesson[`${count.group_id}-${count.lesson_id}`] = count.no_of_students
      return studentCountsByLesson
    }, {})
  }

  const getSubjectStyles = subject => {
    if (subject === "N/A") return { backgroundColor: "#c0bcc2", color: "white" }

    return {
      backgroundColor: stringToColor(subject, "subject"),
      color: "white",
    }
  }

  const getTableData = (session, filter = true) => {
    const bookingCountsByGroupLesson = getBookingCountsByGroupLesson(session)
    const studentCountsByGroupLesson = getStudentCountsByGroupLesson(session)

    const data = session?.groups.reduce((data, group) => {
      if (group.lessons.length > 0) {
        group?.lessons.forEach(lesson => {
          data.push({ 
            id: `${group.id}-${lesson.id}`,
            lessonId: lesson.id,
            groupId: group.id, 
            userId: group.user_id,
            hasSBAccount: getHasSBAccount(group.user.email),
            lesson: lesson.name, 
            group: group.title,
            location: group?.location?.name || "N/A",
            locationId: group?.location?.id || null,
            studentCount: lesson.studentCount,
            isUnassigned: group.user_id === user?.me?.client?.unassignedStaffMember?.user_id,
            groupLessonStudentCount: studentCountsByGroupLesson[`${group.id}-${lesson.id}`] || 0,
            subject: lesson?.subject?.name || "N/A",
            groupLessonBookingCount: bookingCountsByGroupLesson[`${group.id}-${lesson.id}`] || 0,
          })
        })
      }

      return data
    }, [])

    if (filter) {
      return data.filter(item => isInFilter(item))
    }

    return data
  }

  const getLessonIdsByUserId = (lessons) => {
    const groupedLessons = groupBy(lessons, (lesson) => lesson.teacher.user_id);

    return map(groupedLessons, (lessons, userId) => ({
      user_id: userId,
      lesson_ids: map(lessons, (lesson) => lesson.id)
    }));
  }

  const getMissingSBAccounts = (tableData) => {
    const missingSBLessonAccounts = tableData
      .filter(item => item.hasSBAccount === false)
      .map(item => item.userId)

    return [...new Set(missingSBLessonAccounts)]
  }

  const getMoreMenuItems = () => {
    const moreMenuItems = [
      { id: 1, handleClick: () => handleDownloadEventSyncReport(), caption: "Download Event Sync Report" },
    ]

    if (isVenueEvent) {
      moreMenuItems.push({ id: 2, handleClick: () => handleShowAddLocationModal(null, null, true), caption: "Bulk Change Locations", isDisabled: isReadOnlyMode || selectedIds.size === 0 })
    }

    return moreMenuItems
  }

  const getSBAccountIcon = (hasSBAccount) => {
    if (hasSBAccount === null) return <Spinner variant="dots" classNames={{ "wrapper": "-top-2" }}/>
    
    return hasSBAccount
      ? <CheckCircleIcon className="inline-block w-8 h-8 text-green-500" />
      : <XCircleIcon className="inline-block w-8 h-8 text-orange-500" />
  }

  const getSelectedLessonsByUser = () => {
    return session.groups.reduce((lessonsByUser, group) => {
      group.lessons.forEach(lesson => {
        lessonsByUser.push({ lesson_id: lesson.id, user_id: group.user_id })
      })

      return lessonsByUser
    }, [])
  }

  const getSummaryData = (tableData) => {
    return {
      noOfMissingSBAccounts: getMissingSBAccounts(tableData).length,
      noOfLessons: tableData.length,
      noOfGroups: session.groups.length,
      noOfSessions: user.currentEvent.sessions.length,
      noOfBookings: tableData.reduce((total, item) => total + item.groupLessonBookingCount, 0),
      noOfMisStudentMismatches: tableData.reduce((total, item) => total + (item.groupLessonStudentCount !== item.studentCount ? 1 : 0), 0),
      noOfUnassignedLessons: tableData.reduce((total, item) => total + (item.isUnassigned ? 1 : 0), 0),
    }
  }
  
  const handleAddLessons = (lessons) => {
    setAddingMisLessons(true)

    updateSessionLessons({ 
      variables: { 
        input: { 
          session_id: currentSessionId, 
          lessons_to_add: getLessonIdsByUserId(lessons), 
          lessons_to_delete: [] 
        } 
      },
      
      onCompleted: () => {
        setShowLessonSelectorModal(false)
        setAddingMisLessons(false)
      },

      onError: (errors) => {
        setAddingMisLessons(false)
        notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while adding lessons."), open: true })
      }
    })
  }
  
  const handleShowAddLocationModal = (data = null, group = null, isMultiple = false) => {
    setLocationsModal(prevState => ({
      ...prevState,
      group: isMultiple ? null : group, 
      title: isMultiple ? "Bulk Change Locations" : `Add Locations - ${group?.title}`,
      description: isMultiple ? "Select a location to assign to all selected groups" : "Select a location for this group",
      lesson_id: isMultiple ? null : data?.lessonId,
      initial_location_id: isMultiple ? getMultipleInitialLocationId() : group?.location?.id || null,
      isMultiple,
      loading: true 
    }))

    getLocations().then((result) => {
      if (result?.data?.locations) {
        setLocationsModal(prevState => ({ ...prevState, loading: false, show: true }))
      }
    })
  }

  const handleBookSlot = (slotId, studentId, userId, lessonId, meetingTypeId) => {
    setIsBookingSlot(true)

    return createBookingSlot({
      variables: {
        input: {
          event_id: eventId,
          lesson_id: lessonId,
          slot_id: slotId,
          student_id: studentId,
          user_id: userId,
          quantity: 1,
          meeting_type_id: meetingTypeId
        }
      }
    }).finally((response) => setIsBookingSlot(false))
  }
  
  const handleChangeSession = (session) => {
    setCurrentSessionId(session.id)
    closeSessionSelectorSidebar()
  }
  
  const handleCloseGroupBookingModal = (hasMadeBookings) => {
    if (hasMadeBookings) {
      sessionQuery.refetch()
    }
    
    setGroupBookingModal({group: null, show: false, slots: [], loading: false, lessonId: null})
  }

  const handleCloseGroupLessonStudentsDiffModal = (refresh = false) => {
    setGroupLessonStudentsDiffModal(prevState => ({ ...prevState, show: false }))

    if (refresh) {
      sessionQuery.refetch()
    }
  }

  const handleDeleteLessons = (sendCancellationEmails, intro) => {
    setIsDeletingLessons(true)
  
    updateSessionLessons({ 
      variables: { 
        input: { 
          session_id: currentSessionId,
          lessons_to_add: [],
          lessons_to_delete: getLessonsToDeletePayload(), 
          send_cancellation_emails: sendCancellationEmails, 
          cancellation_email_intro: intro 
        }
      },

      onCompleted: () => {
        setIsDeletingLessons(false)
        setDeleteConfirmationModal({ bookingSlotIds: [], totalBookingSlots: 0, show: false, error: null })
        setSelectedIds(new Set([]))

        if (sendCancellationEmails) {
          setShowNotificationEmailsQueuedModal(true)
        }
      },

      onError: ({ graphQLErrors }) => {
        setDeleteConfirmationModal(prevState => ({ ...prevState, error: graphQLErrors[0] }))
        setIsDeletingLessons(false)
      }
    })
  }

  const handleDownload = type => {
    if (type === "xls") {
      setIsDownloading(true)
      getSessionGroupsXlsDownloadLink()
    }
  }
  
  const handleDownloadEventSyncReport = () => {
    setIsDownloadingEventSyncReport(true)
    getEventSyncReportDownloadLink({ variables: { eventId } }).finally(() => setIsDownloadingEventSyncReport(false))
  }

  const handleDownloadGroupBookingsXls = (groupId, lessonIds) => {
    setIsDownloadingGroupBookingsXls(true)
    getGroupBookingsXlsDownloadLink({ variables: { input: { event_id: user.currentEvent.id, group_id: groupId, lesson_ids: lessonIds } } })
  }

  const handleDuplicateGroupLessons = (group, selectedLessonIds, userId) => {
    setIsDuplicating(true)

    duplicateGroupLessons({ 
      variables: { 
        input: { 
          client_id: user.me.client.id,
          event_id: user.currentEvent.id,
          session_id: currentSessionId,
          source_group_id: group.id, 
          source_lesson_ids: selectedLessonIds, 
          user_id: userId 
        } 
      }
    })
  }

  const handlePreviewEmail = (bookingSlotIds, intro) => {
    getEmailPreview({
      variables: {
        input: {
          event_id: eventId,
          booking_slot_ids: bookingSlotIds,
          intro: intro
        }
      }
    }).then(() => {
      setShowEmailPreviewModal(true)
    })
  }

  const handleReassignGroupLessons = (group, selectedLessonIds, userId) => {
    setIsReassigning(true)

    reassignGroupLessons({ 
      variables: { 
        input: { 
          client_id: user.me.client.id,
          event_id: user.currentEvent.id,
          session_id: currentSessionId,
          source_group_id: group.id, 
          source_lesson_ids: selectedLessonIds, 
          user_id: userId 
        } 
      }
    })
  }

  const handleRefetchData = () => {
    setRefetching(true)
    
    Promise.all([
      sessionQuery.refetch(),
      refreshAccounts()
    ]).finally(() => {
      setRefetching(false)
    })
  }

  const handleRowSelection = id => {
    selectedIds.has(id)
      ? setSelectedIds(previousIds => new Set([...previousIds].filter(_id => _id !== id)))
      : setSelectedIds(previousIds => new Set([...previousIds, id]))
  }

  const handleSaveGroupLessonNote = (group, lesson, note) => {
    setIsUpdatingGroupLessonNote(true)

    note.trim() === ""
      ? deleteGroupLessonNote({ variables: { input: { group_id: group.id, lesson_id: lesson.id } } })
      : updateGroupLessonNote({ variables: { input: { group_id: group.id, lesson_id: lesson.id, note } } })
  }

  const handleSaveMultipleGroupLocations = (locationId) => {
    const groupIds = [...new Set([...selectedIds].map(item => parseInt(item.split('-')[0])))];
    const variables = { input: { group_ids: groupIds, location_id: locationId, event_id: eventId } }

    updateGroupsLocation({ variables }).then((response) => {
      if (response?.data?.updateGroupsLocation) {
        setLocationsModal(prevState => ({ ...prevState, title: null, description: null, group: null, lesson: null, show: false }))
      }
    })
  }

  const handleSaveSingleGroupLocation = (group, locationId) => {
    setIsUpdatingGroup(true)

    updateGroup({ variables: { input: { group_id: group.id, location_id: locationId } } }).then((response) => {
      if (response?.data?.updateGroup) {
        setLocationsModal(prevState => ({ ...prevState, show: false }))
      }
    })
  }
  
  const handleSaveGroupTitle = (group, title) => {
    setIsUpdatingGroup(true)

    updateGroup({ variables: { input: { group_id: group.id, title } } }).then((response) => {
      if (response?.data?.updateGroup) {
        setEditGroupTitleModal({ group: null, show: false })
      }
    })
  }

  const handleShowDeleteLessonsConfirmation = () => {
    sessionGroupLessonBookingCounts().then(({ data }) => { 
      const bookingSlotIds = data.session.groupLessonBookingCounts
        .filter(groupLesson => selectedIds.has(`${groupLesson.group_id}-${groupLesson.lesson_id}`))
        .flatMap(item => item.booking_slot_ids)
      
      setDeleteConfirmationModal({ totalBookingSlots: bookingSlotIds.length, bookingSlotIds, show: true, error: null })
    })    
  }

  const handleShowDuplicateModal = (data, group) => {
    const selectedLesson = group.lessons.find(lesson => lesson.id === data.lessonId)
    setDuplicateModal({ group, lessons: group.lessons, selectedLessons: [selectedLesson], show: true })
  }

  const handleEditGroupTitleModal = (data, group) => {
    setEditGroupTitleModal({ group, show: true })
  }
  
  const handleShowGroupLessonNoteModal = (data, group) => {
    const selectedLesson = group.lessons.find(lesson => lesson.id === data.lessonId)
    setGroupLessonNoteModal({ group, lesson: selectedLesson, groupLessonNote: group.groupLessonNotes.find(note => note.lesson_id === data.lessonId), show: true })
  }
  
  const handleShowGroupBookingsModal = (data, group) => {
    setGroupBookingModal(prevState => ({ ...prevState, group, lessonId: data.lessonId, loading: true }))

    getGroupSlots({ variables: { id: group.id }})
      .then((response) => {
        const slots = response?.data?.group?.slots

        if (slots) {
          const groupSlots = slots.map(slot => ({
            ...slot,
            starts_at: mySqlToLocal(slot.starts_at),
            ends_at: mySqlToLocal(slot.ends_at),
          }))

          setGroupBookingModal({ group: group, show: true, slots: groupSlots, loading: false, lessonId: data.lessonId, lessonName: data.lesson })
        }
      })
  }

  const handleShowGroupLessonStudentsDiffModal = (data) => {
    setGroupLessonStudentsDiffModal({ data, show: true })
  }

  const handleShowReassignModal = (data, group) => {
    const selectedLesson = group.lessons.find(lesson => lesson.id === data.lessonId)
    setReassignModal({ group, lessons: group.lessons, selectedLessons: [selectedLesson], show: true })
  }

  const handleToggleColumn = selectedColumns => {
    setSelectedColumns(Object.keys(columns).filter(column => selectedColumns.has(column)))
  }

  const handleShowSessionsSidebar = () => {
    openSessionSelectorSidebar({ sessions: user.currentEvent.sessions, handleChangeSession })
  }

  const handleToggleFilter = selectedFilter => {
    setSelectedFilters([...selectedFilter])
  }

  const isInFilter = (item) => {
    const search = searchTerm.toLowerCase()
    return item.group.toLowerCase().includes(search) || item.lesson.toLowerCase().includes(search) || item?.subject?.toLowerCase().includes(search)
  }

  const renderCell = (data, column, group, isVenueEvent) => {
    if (column === "select") {
      return <Checkbox color="secondary" isSelected={selectedIds.has(data.id)} onChange={() => handleRowSelection(data.id)} />
    }

    const studentsColor = data.groupLessonStudentCount === data.studentCount ? "success" : "warning"
    const isGroupBookingModalLoading = groupBookingModal.loading && group.id === groupBookingModal.group?.id && groupBookingModal.lessonId === data.lessonId
    const isLocationsModalLoading = locationsModal.loading && !locationsModal.isMultiple && locationsModal.group?.id === group.id && locationsModal.lesson_id === data.lessonId

    const columnMapping = {
      group: (
        <div className="flex items-center gap-2">
          <span>{data.group}</span>  
          <HelpPopover id={`${data.id}`}>
            <p className="mb-2">Group Owner: <span className="font-semibold">{group?.user?.name || 'Unknown'}</span></p>
            <p className="mb-2">SB Account: <span className="font-semibold">{data.hasSBAccount ? group?.user?.email : 'No Match'}</span></p>
            <p className="mb-2">Staff Email: <span className="font-semibold">{group?.user?.email || 'None'}</span></p>
          </HelpPopover>
        </div>
      ),
      hasSBAccount: getSBAccountIcon(data.hasSBAccount),
      lesson: data.lesson,
      subject: <Chip size="md" radius="sm" style={getSubjectStyles(data.subject)}>{data.subject}</Chip>,
      location: data?.location || "N/A",
      studentCounts: <Chip className="text-center text-white min-w-16" size="md" color={studentsColor}>{`${data?.groupLessonStudentCount || 0} / ${data?.studentCount || 0}`}</Chip>,
      bookingCounts: `${data?.groupLessonBookingCount || 0}`,
      actions: (
        <EllipsisDropdown 
          items={[
            { id: 1, handleClick: () => handleShowAddLocationModal(data, group), caption: "Add/Edit Location", isDisabled: isReadOnlyMode || !isVenueEvent },
            { id: 2, handleClick: () => handleEditGroupTitleModal(data, group), caption: "Edit Group Title", isDisabled: isReadOnlyMode },
            { id: 3, handleClick: () => handleShowDuplicateModal(data, group), caption: "Duplicate Lesson(s)", isDisabled: isReadOnlyMode },
            { id: 4, handleClick: () => handleShowGroupLessonNoteModal(data, group), caption: "Lesson Notes" },
            { id: 5, handleClick: () => handleShowReassignModal(data, group), caption: "Reassign Lesson", isDisabled: isReadOnlyMode },
            { id: 6, handleClick: () => handleShowGroupLessonStudentsDiffModal(data), caption: "View/Edit Students" },
            { id: 7, handleClick: () => handleShowGroupBookingsModal(data, group), caption: "Make Bookings", isDisabled: isReadOnlyMode },
          ]}
    
          isLoading={isGroupBookingModalLoading || isLocationsModalLoading}
        />
      )
    }
      
    return columnMapping[column]
  }
  
  const session = sessionQuery?.data?.session || {}
  const isLoading = !currentSessionId || sessionQuery.loading
  const deleteButtonCaption = selectedIds.size === 0 ? "Delete" : `Delete ${selectedIds.size} Lessons`
  const defaultMeetingType = user?.currentEvent?.meetingTypes && user?.currentEvent?.meetingTypes.length === 1 ? user?.currentEvent?.meetingTypes[0] : null
  const hasSelection = selectedIds.size > 0
  const groupsById = !isLoading ? getGroupsById(session.groups) : {}
  const tableData = !isLoading ? getTableData(session) : []
  const filteredOrderedTableData = !isLoading ? getFilteredOrderedTableData(tableData) : []
  const summary = !isLoading ? getSummaryData(tableData) : {}
  const isReadOnlyMode = !isEmpty(session) ? hasExpired(mySqlToLocal(session.ends_at)) : true
  const isVenueEvent = user?.currentEvent?.meetingTypes?.some(mt => mt.type === "VENUE")
  const visibleSelectedColumns = isVenueEvent ? selectedColumns : selectedColumns.filter(col => col !== "location")
  const moreMenuItems = getMoreMenuItems()

  const noDataMessage = isReadOnlyMode || session?.groups?.length > 0 
    ? "No Lessons found! Try clearing any filters." 
    : <span className="flex flex-col items-center gap-4">
        Get started by adding lessons to your event. 
        {
        <Button className="font-semibold text-white" size="lg" color="success" onPress={() => setShowLessonSelectorModal(true)} isDisabled={isReadOnlyMode}>
          Add Your First Lesson
        </Button>
        }
      </span>

  return (
    <EventViewsTracker eventId={eventId}>
      <Page>
        <EventPageHeader
          title="Manage Groups"
          icon={<RectangleGroupIcon className="page-header-icon" />}
          event={user?.currentEvent}
          actions={<EventMenu event={user?.currentEvent} user={user} />}
        />
          
        <Stats summary={summary} />

        <DataTable
          actions={
            <div className="flex gap-2">
              <Button color="primary" onPress={() => setShowLessonSelectorModal(true)} isDisabled={isReadOnlyMode}>Add</Button>
              <Button color="danger" className="shrink-0" isDisabled={!hasSelection || isReadOnlyMode} onPress={() => handleShowDeleteLessonsConfirmation()}>{deleteButtonCaption}</Button>
              <EllipsisDropdown items={moreMenuItems} size="md" isLoading={isDownloadingEventSyncReport || (locationsModal.loading && locationsModal.isMultiple)} />
            </div>
          }
          columns={columns}
          currentSortOrder={sortDescriptor}  
          data={filteredOrderedTableData}
          disallowEmptyFilterSelection={false}
          downloadOptions={downloadOptions}
          filters={filters}
          filtersSelectionMode="single"
          handleDownload={type => handleDownload(type)}
          handleRefetch={() => handleRefetchData()}
          handleRenderCell={(item, key) => renderCell(item, key, groupsById[item.groupId], isVenueEvent)}
          handleSearchChange={searchTerm => setSearchTerm(searchTerm)}
          handleSearchClear={() => setSearchTerm("")}
          handleSortChange={sortDescriptor => setSortDescriptor(sortDescriptor)}
          handleToggleColumn={selectedColumns => handleToggleColumn(selectedColumns)}
          handleToggleFilter={selectedFilters => handleToggleFilter(selectedFilters)}
          header={<DataTableSessionHeader session={session} handleShowSessionsSidebar={handleShowSessionsSidebar} showChangeSessionButton={user.currentEvent.sessions.length > 1} />}
          isDownloading={isDownloading}
          loading={isLoading}
          isRefetching={refetching}
          noDataMessage={<EmptyPlaceholder classNames="mt-2" title="No Lessons Found" description={noDataMessage} />}
          searchInputPlaceHolder="Search by group / lesson / subject"
          searchTerm={searchTerm}
          selectedColumns={visibleSelectedColumns}
          selectedFilters={selectedFilters}
          showColumnsButton={true}
          showDownloadButton={true}
          showFilterCount={true}
          showPagination={false}
          showRefetchButton={true}
        />

        {deleteConfirmationModal.show && 
          <DeleteLessonsConfirmationModal
            error={deleteConfirmationModal.error}
            handleClose={() => setDeleteConfirmationModal(prevState => ({ ...prevState, show: false, error: null }))}
            handleDelete={(sendCancellationEmails, intro) => handleDeleteLessons(sendCancellationEmails, intro)}
            handlePreviewEmail={(bookingSlotIds, intro) => handlePreviewEmail(bookingSlotIds, intro)}
            isDeleting={isDeletingLessons}
            isLoadingPreview={isLoadingCancellationEmailPreview}
            noOfBookingSlotsToDelete={deleteConfirmationModal.totalBookingSlots}
            noOfLessonsToDelete={selectedIds.size}
            show={deleteConfirmationModal.show} 
            bookingSlotIds={deleteConfirmationModal.bookingSlotIds}
          />
        }
        
        <NotificationEmailsQueuedModal
          event={user?.currentEvent}
          handleClose={() => setShowNotificationEmailsQueuedModal(false)}
          show={showNotificationEmailsQueuedModal}
          title="Booking Deletion - Emails Queued"
        />

        {duplicateModal.show && 
          <DuplicateModal 
            client={user.me.client}
            group={duplicateModal.group} 
            lessons={duplicateModal.lessons} 
            selectedLessons={duplicateModal.selectedLessons}
            handleClose={() => setDuplicateModal(prevState => ({ ...prevState, show: false }))}
            show={duplicateModal.show}
            handleSave={(group, selectedLessonIds, userId) => handleDuplicateGroupLessons(group, selectedLessonIds, userId)}
            isSaving={isDuplicating}
          />
        }
        
        {editGroupTitleModal.show && (
          <EditGroupTitleModal 
            handleClose={() => setEditGroupTitleModal(prevState => ({ ...prevState, show: false }))}
            handleSave={(group, title) => handleSaveGroupTitle(group, title)}
            isSaving={isUpdatingGroup}
            show={editGroupTitleModal.show}
            group={editGroupTitleModal.group}
            header={`Group owned by ${editGroupTitleModal.group?.user?.name || "Unknown"}`}
          />
        )}
        
        {showEmailPreviewModal && 
          <EmailPreviewModal 
            handleClose={() => setShowEmailPreviewModal(false)}
            html={cancellationEmailPreview?.previewDeletedBookingSlotsEmail?.html} 
            isLoading={isLoadingCancellationEmailPreview} 
            show={showEmailPreviewModal}
            title="Delete Appointments Email Preview"
          />
        }

        {groupBookingModal.show && 
          <GroupBookingModal
            eventId={eventId}
            groupId={groupBookingModal.group?.id}
            lessonId={groupBookingModal.lessonId}
            handleBookSlot={(slotId, studentId, userId, lessonId, meetingTypeId) => handleBookSlot(slotId, studentId, userId, lessonId, meetingTypeId)}
            isSaving={isBookingSlot}
            handleClose={(hasMadeBookings) => handleCloseGroupBookingModal(hasMadeBookings)}
            selectedSlotId={null}
            initialSlots={groupBookingModal.slots || []}
            defaultMeetingType={defaultMeetingType}
            meetingTypes={user?.currentEvent?.meetingTypes || []}
            show={true}
            title={`${groupBookingModal.group?.title} - ${groupBookingModal.lessonName}`}
          />
        }

        {groupLessonNoteModal.show && 
          <GroupLessonNoteModal
            group={groupLessonNoteModal.group} 
            groupLessonNote={groupLessonNoteModal.groupLessonNote} 
            lesson={groupLessonNoteModal.lesson} 
            handleClose={() => setGroupLessonNoteModal(prevState => ({ ...prevState, show: false }))}
            show={groupLessonNoteModal.show}
            handleSave={(group, lesson, note) => handleSaveGroupLessonNote(group, lesson, note)}
            isReadOnly={isReadOnlyMode}
            isSaving={isUpdatingGroupLessonNote}
          />
        }

        {groupLessonStudentsDiffModal.show && (
          <GroupLessonStudentsDiffModal
            clientId={user.me.client.id}
            groupId={groupLessonStudentsDiffModal.data.groupId} 
            handleClose={(refresh) => handleCloseGroupLessonStudentsDiffModal(refresh)}
            handlePreviewEmail={(bookingSlotIds, intro) => handlePreviewEmail(bookingSlotIds, intro)}
            isPreviewEmailModalVisible={showEmailPreviewModal}
            isLoadingPreview={isLoadingCancellationEmailPreview}
            isReadOnly={isReadOnlyMode}
            lessonId={groupLessonStudentsDiffModal.data.lessonId}
            sessionId={currentSessionId}
            show={groupLessonStudentsDiffModal.show}
            title={`Students in ${groupLessonStudentsDiffModal.data.group} - ${groupLessonStudentsDiffModal.data.lesson}`}
          />
        )}

        {showLessonSelectorModal && 
          <LessonSelectorModal 
            clientId={user.me.client.id} 
            show={showLessonSelectorModal} 
            handleClose={() => setShowLessonSelectorModal(false)} 
            handleSubmit={(selectedLessons) => handleAddLessons(selectedLessons)}
            isSubmitting={addingMisLessons}
            lessons={session.lessons}
            selectedLessonsByUser={getSelectedLessonsByUser()}
          />
        }

        {locationsModal.show &&
          <LocationsModal 
            description={locationsModal.description}
            locations={locations?.locations || []}
            handleClose={() => setLocationsModal(prevState => ({ ...prevState, group: null, title: null, description: null, lesson: null, lesson_id: null, show: false }))}
            handleSave={(locationId) => {locationsModal.isMultiple ? handleSaveMultipleGroupLocations(locationId) : handleSaveSingleGroupLocation(locationsModal.group, locationId)}}
            initialLocation={locationsModal.initial_location_id}
            isSaving={locationsModal.isMultiple ? isUpdatingGroupsLocation : isUpdatingGroup}
            show={locationsModal.show}
            title={locationsModal.title}
          />
        }
      
        {reassignModal.show && 
          <ReassignModal 
            client={user.me.client}
            group={reassignModal.group} 
            lessons={reassignModal.lessons} 
            selectedLessons={reassignModal.selectedLessons}
            handleClose={() => setReassignModal(prevState => ({ ...prevState, show: false }))}
            show={reassignModal.show}
            handleSave={(group, selectedLessonIds, userId) => handleReassignGroupLessons(group, selectedLessonIds, userId)}
            handleDownload={(groupId, lessonIds) => handleDownloadGroupBookingsXls(groupId, lessonIds)}
            isDownloading={isDownloadingGroupBookingsXls}
            isSaving={isReassigning}
          />
        }
      </Page>
    </EventViewsTracker>
  )
}

export default ManageGroups
